import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, LabelList
} from 'recharts';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import DemoBlockModal from '../components/DemoBlockModal';
import { isDemoUser } from '../utils/demoGuard';
import './DemographicsDashboard.css';
import { BIGQUERY_CONFIG } from '../config';

import { API_BASE_URL } from '../config';

// Use config values
const PROJECT_ID = BIGQUERY_CONFIG.PROJECT;
const DATASET_NAME = BIGQUERY_CONFIG.DATASET;
const TABLE_NAME = BIGQUERY_CONFIG.TABLE;

// Formatter functions
const formatCurrency = (value) => `$${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const formatLOS = (value) => (Number(value || 0)).toFixed(1);
const formatRiskScore = (value) => (Number(value || 0)).toFixed(3);
const formatNumber = (value) => Number(value || 0).toLocaleString('en-US');

// Sample data for demonstration - replace with actual BigQuery data
const sampleData = {
  membershipByCounty: [
    { name: 'County A', value: 3500 },
    { name: 'County B', value: 2000 },
    { name: 'County C', value: 1500 },
    { name: 'County D', value: 1000 }
  ],
  membershipByRisk: [
    { name: '<1', value: 500 },
    { name: '1-1.5', value: 800 },
    { name: '1.5-2', value: 1500 },
    { name: '2-2.5', value: 2800 },
    { name: '>3', value: 2400 }
  ],
  deathsPerMonth: [
    { month: 'Jan', deaths: 250 },
    { month: 'Feb', deaths: 300 },
    { month: 'Mar', deaths: 200 },
    { month: 'Apr', deaths: 350 },
    { month: 'May', deaths: 280 },
    { month: 'Jun', deaths: 320 },
    { month: 'Jul', deaths: 400 },
    { month: 'Aug', deaths: 450 },
    { month: 'Sep', deaths: 380 },
    { month: 'Oct', deaths: 320 },
    { month: 'Nov', deaths: 280 },
    { month: 'Dec', deaths: 240 }
  ],
  membershipByCohort: [
    { name: 'A', value: 250, color: '#0369a1' },
    { name: 'B', value: 150, color: '#0284c7' },
    { name: 'C', value: 200, color: '#0891b2' },
    { name: 'Other', value: 400, color: '#bae6fd' }
  ],
  membershipByGender: [
    { name: 'Male', value: 3564, color: '#0369a1' },
    { name: 'Female', value: 4436, color: '#67e8f9' }
  ],
  membershipByAge: [
    { name: '<45', value: 500 },
    { name: '45-64', value: 1500 },
    { name: '65-74', value: 1800 },
    { name: '75-84', value: 1500 },
    { name: '>85', value: 700 }
  ]
};

function DemographicsDashboard() {
  const { user, getAuthHeader } = useAuth();
  const [showDemoBlock, setShowDemoBlock] = React.useState(false);
  const [metrics, setMetrics] = useState({
    totalBenes: 0,
    personYears: 0,
    avgRiskScore: 0,
    totalDeaths: 0,
    avgAge: 0
  });
  const [data, setData] = useState({
    membershipByCounty: [],
    membershipByRisk: [],
    deathsPerMonth: [],
    membershipByCohort: [],
    membershipByGender: [],
    membershipByAge: []
  });
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [filters, setFilters] = useState({
    'BENE_MBI_ID': ['All'],
    'BENE_1ST_NAME': ['All'],
    'BENE_LAST_NAME': ['All'],
    'GENDER': ['All'],
    'COUNTY': ['All'],
    'COHORT': ['All'],
    'CHRONIC': ['All'],
    'DEATH': ['All'],
    'STATE': ['All']
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [filters]);

  const handleFilterChange = (field, values) => {
    console.log(`Filter changed - Field: ${field}, Values:`, values);
    setFilters(prev => ({ ...prev, [field]: values }));
  };

  const handleResetFilters = () => {
    setFilters({
      'BENE_MBI_ID': ['All'],
      'BENE_1ST_NAME': ['All'],
      'BENE_LAST_NAME': ['All'],
      'GENDER': ['All'],
      'COUNTY': ['All'],
      'COHORT': ['All'],
      'CHRONIC': ['All'],
      'DEATH': ['All'],
      'STATE': ['All']
    });
  };

  const buildFilterParams = () => {
    let params = `project=${PROJECT_ID}&dataset=${DATASET_NAME}&table=${TABLE_NAME}`;
    
    // Add filter parameters if not 'All'
    Object.entries(filters).forEach(([field, values]) => {
      if (!values.includes('All') && values.length > 0) {
        const filterValue = values.join(',');
        params += `&${field.toLowerCase()}=${encodeURIComponent(filterValue)}`;
      }
    });
    
    return params;
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const params = buildFilterParams();
      
      console.log('Fetching combined data with params:', params);
      console.log('API Base URL:', API_BASE_URL);
      console.log('[DASHBOARD DEBUG] localStorage token:', localStorage.getItem('jwt_token') ? 'EXISTS' : 'MISSING');
      
      // Axios interceptor automatically adds Authorization header
      const combinedRes = await axios.get(
        `${API_BASE_URL}/api/demographics/combined?${params}`,
        { timeout: 180000 }
      );
      
      console.log('Combined Response:', combinedRes.data);
      
      const responseData = combinedRes.data.data;
      
      // Update metrics
      setMetrics(responseData.metrics);
      
      // Fetch beneficiaries separately with limit=15 (don't fail if this fails)
      try {
        const beneficiariesRes = await axios.get(`${API_BASE_URL}/api/demographics/beneficiaries?${params}&limit=15`, { 
          timeout: 60000
        });
        setBeneficiaries(beneficiariesRes.data.data);
      } catch (benefError) {
        console.error('Error loading beneficiaries:', benefError);
        setBeneficiaries([]);
      }
      
      // Calculate percentages for charts
      const totalBenes = responseData.metrics.total_benes;
      
      const addPercentages = (dataArray) => {
        return dataArray.map(item => ({
          ...item,
          percentage: ((item.value / totalBenes) * 100).toFixed(1)
        }));
      };
      
      // Update all charts
      setData({
        membershipByCounty: addPercentages(responseData.by_county), // Already in DESC order from SQL
        membershipByRisk: addPercentages(responseData.by_risk), // Ordered 0-1, 1-2, 2-3, >3
        deathsPerMonth: responseData.by_month,
        membershipByCohort: addPercentages(responseData.by_cohort),
        membershipByGender: addPercentages(responseData.by_gender),
        membershipByAge: addPercentages(responseData.by_age) // Ordered <45, 45-64, 65-74, 75-84, 85+
      });
      
      console.log('Data loaded successfully!');
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      console.error('Error details:', error.response?.data || error.message);
      alert('Failed to load data. Check console for details. Error: ' + (error.response?.data?.detail || error.message));
      setLoading(false);
    }
  };

  const downloadCSV = async () => {
    if (isDemoUser(user)) { setShowDemoBlock(true); return; }
    try {
      // Fetch ALL rows for CSV download (no limit)
      const params = buildFilterParams();
      const response = await axios.get(`${API_BASE_URL}/api/demographics/beneficiaries`, {
        params: {
          ...params,
          limit: 0  // 0 means no limit, fetch all rows
        }
      });
      
      if (response.data && response.data.data && response.data.data.length > 0) {
        const allBeneficiaries = response.data.data;
        const headers = Object.keys(allBeneficiaries[0]);
        const csvData = allBeneficiaries.map(row => 
          headers.map(header => row[header] || '')
        );
        
        const csvContent = [
          headers.join(','),
          ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'beneficiaries.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Failed to download CSV. Please try again.');
    }
  };

  const COLORS = ['#0369a1', '#0284c7', '#0891b2', '#67e8f9'];

  if (loading) {
    return (
      <div className="page-with-sidebar">
        <Sidebar filters={filters} onFilterChange={handleFilterChange} onResetFilters={handleResetFilters} />
        <div className="dashboard">
          <h1 className="dashboard-title">👥 Demographics Dashboard</h1>
          <div className="metrics-row">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} style={{
                background: 'white',
                padding: '15px',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                flex: 1,
                minWidth: '180px',
                animation: 'pulse 1.5s ease-in-out infinite',
                animationDelay: `${i * 0.1}s`
              }}>
                <div style={{height: '14px', backgroundColor: '#d1d5db', borderRadius: '4px', marginBottom: '8px', width: '60%'}}></div>
                <div style={{height: '28px', backgroundColor: '#e5e7eb', borderRadius: '4px', width: '80%'}}></div>
              </div>
            ))}
          </div>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginTop: '25px'}}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} style={{
                background: 'white',
                padding: '15px',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                minHeight: '430px',
                animation: 'pulse 1.5s ease-in-out infinite',
                animationDelay: `${i * 0.15}s`
              }}>
                <div style={{height: '20px', backgroundColor: '#d1d5db', borderRadius: '4px', marginBottom: '16px', width: '50%'}}></div>
                <div style={{height: '380px', backgroundColor: '#e5e7eb', borderRadius: '4px'}}></div>
              </div>
            ))}
          </div>
          <style>{`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
          `}</style>
        </div>
      </div>
    );
  }

  return (
    <div className="page-with-sidebar">
      {showDemoBlock && <DemoBlockModal onClose={() => setShowDemoBlock(false)} />}
      <Sidebar filters={filters} onFilterChange={handleFilterChange} onResetFilters={handleResetFilters} />
      <div className="dashboard">
      <h1 className="dashboard-title">👥 Demographics Dashboard</h1>
      
      <div style={{animation: 'fadeIn 0.5s ease-in-out'}}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div className="metrics-row">
        <div className="metric-card">
          <div className="metric-label" style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
            <span style={{fontSize: '14px'}}>👥</span> TOTAL BENES
          </div>
          <div className="metric-value">{metrics.total_benes?.toLocaleString() || '0'}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label" style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
            <span style={{fontSize: '14px'}}>📅</span> PERSON YEARS
          </div>
          <div className="metric-value">{metrics.person_years?.toLocaleString() || '0'}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label" style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
            <span style={{fontSize: '14px'}}>📊</span> AVG RISK SCORE
          </div>
          <div className="metric-value">{(metrics.avg_risk_score || 0).toFixed(3)}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label" style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
            <span style={{fontSize: '14px'}}>⚕️</span> TOTAL DEATHS
          </div>
          <div className="metric-value">
            {metrics.total_deaths?.toLocaleString() || '0'} 
            <span style={{fontSize: '16px', color: '#6b7280', marginLeft: '8px'}}>
              ({metrics.death_percentage || '0'}%)
            </span>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label" style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
            <span style={{fontSize: '14px'}}>🎂</span> AVG AGE
          </div>
          <div className="metric-value">{Math.round(metrics.avg_age) || '0'}</div>
        </div>
      </div>

      <div className="charts-grid">
        <div className="chart-card" style={{padding: '15px 0px 15px 0px'}}>
          <h3 style={{margin: '0 0 15px 20px'}}>
            Membership By County
            {data.membershipByCounty.length > 15 && (
              <span style={{fontSize: '14px', color: '#6b7280', fontWeight: 'normal', marginLeft: '8px'}}>
                (Top 15 of {data.membershipByCounty.length})
              </span>
            )}
          </h3>
          <ResponsiveContainer width="100%" height={450}>
            <BarChart data={data.membershipByCounty.slice(0, 15)} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={180} style={{fontSize: '12px'}} interval={0} tick={{dy: 4}} />
              <Tooltip 
                cursor={{ fill: '#e0f2fe' }}
                content={({active, payload}) => {
                if (active && payload && payload.length) {
                  return (
                    <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '8px', borderRadius: '4px', fontSize: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
                      <p style={{margin: 0}}><strong>{payload[0].payload.name}</strong></p>
                      <p style={{margin: '4px 0 0 0'}}>Count: {payload[0].value.toLocaleString()} ({payload[0].payload.percentage}%)</p>
                    </div>
                  );
                }
                return null;
              }} />
              <Bar dataKey="value" fill="#0284c7">
                <LabelList dataKey="value" position="insideRight" formatter={(value) => value.toLocaleString()} style={{fontSize: '10px', fill: 'white'}} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card" style={{padding: '15px 0px 15px 0px'}}>
          <h3 style={{margin: '0 0 15px 20px'}}>Membership By Risk Score Grouping</h3>
          <ResponsiveContainer width="100%" height={450}>
            <BarChart data={data.membershipByRisk} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" style={{fontSize: '12px'}} />
              <YAxis style={{fontSize: '12px'}} />
              <Tooltip 
                cursor={{ fill: '#e0f2fe' }}
                content={({active, payload}) => {
                if (active && payload && payload.length) {
                  return (
                    <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '8px', borderRadius: '4px', fontSize: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
                      <p style={{margin: 0}}><strong>{payload[0].payload.name}</strong></p>
                      <p style={{margin: '4px 0 0 0'}}>Count: {payload[0].value.toLocaleString()} ({payload[0].payload.percentage}%)</p>
                    </div>
                  );
                }
                return null;
              }} />
              <Bar dataKey="value" fill="#0284c7">
                <LabelList dataKey="value" position="inside" formatter={(value) => value.toLocaleString()} style={{fontSize: '12px', fill: 'white'}} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card" style={{padding: '15px 0px 15px 0px'}}>
          <h3 style={{margin: '0 0 15px 20px'}}>Deaths Per Month</h3>
          <ResponsiveContainer width="100%" height={450}>
            <LineChart data={data.deathsPerMonth} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis type="number" domain={['dataMin - 20', 'dataMax + 20']} />
              <Tooltip content={({active, payload}) => {
                if (active && payload && payload.length) {
                  const total = metrics.total_deaths || 1;
                  const percentage = ((payload[0].value / total) * 100).toFixed(1);
                  return (
                    <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '10px', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
                      <p style={{margin: 0}}><strong>{payload[0].payload.month}</strong></p>
                      <p style={{margin: '5px 0 0 0'}}>Deaths: {payload[0].value.toLocaleString()} ({percentage}%)</p>
                    </div>
                  );
                }
                return null;
              }} />
              <Line type="monotone" dataKey="deaths" stroke="#0369a1" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card" style={{padding: '15px 0px 15px 0px'}}>
          <h3 style={{margin: '0 0 15px 20px'}}>Membership By Cohort</h3>
          <ResponsiveContainer width="100%" height={450}>
            <BarChart data={data.membershipByCohort} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" style={{fontSize: '12px'}} />
              <YAxis style={{fontSize: '12px'}} />
              <Tooltip 
                cursor={{ fill: '#e0f2fe' }}
                content={({active, payload}) => {
                if (active && payload && payload.length) {
                  return (
                    <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '8px', borderRadius: '4px', fontSize: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
                      <p style={{margin: 0}}><strong>{payload[0].payload.name}</strong></p>
                      <p style={{margin: '4px 0 0 0'}}>Count: {payload[0].value.toLocaleString()} ({payload[0].payload.percentage}%)</p>
                    </div>
                  );
                }
                return null;
              }} />
              <Bar dataKey="value" fill="#0284c7">
                <LabelList dataKey="value" position="inside" formatter={(value) => value.toLocaleString()} style={{fontSize: '12px', fill: 'white'}} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card" style={{padding: '15px 0px 15px 0px'}}>
          <h3 style={{margin: '0 0 15px 20px'}}>Membership By Gender</h3>
          <ResponsiveContainer width="100%" height={450}>
            <PieChart>
              <Pie
                data={data.membershipByGender}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value, percent }) => `${name}\n${value.toLocaleString()}\n${(percent * 100).toFixed(0)}%`}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {data.membershipByGender.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={({active, payload}) => {
                if (active && payload && payload.length) {
                  return (
                    <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '10px', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
                      <p style={{margin: 0}}><strong>{payload[0].name}</strong></p>
                      <p style={{margin: '5px 0 0 0'}}>Count: {payload[0].value.toLocaleString()} ({payload[0].payload.percentage}%)</p>
                    </div>
                  );
                }
                return null;
              }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card" style={{padding: '15px 0px 15px 0px'}}>
          <h3 style={{margin: '0 0 15px 20px'}}>Membership By Age Group</h3>
          <ResponsiveContainer width="100%" height={450}>
            <BarChart data={data.membershipByAge} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" style={{fontSize: '12px'}} />
              <YAxis style={{fontSize: '12px'}} />
              <Tooltip 
                cursor={{ fill: '#e0f2fe' }}
                content={({active, payload}) => {
                if (active && payload && payload.length) {
                  return (
                    <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '8px', borderRadius: '4px', fontSize: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
                      <p style={{margin: 0}}><strong>{payload[0].payload.name}</strong></p>
                      <p style={{margin: '4px 0 0 0'}}>Count: {payload[0].value.toLocaleString()} ({payload[0].payload.percentage}%)</p>
                    </div>
                  );
                }
                return null;
              }} />
              <Bar dataKey="value" fill="#0284c7">
                <LabelList dataKey="value" position="inside" formatter={(value) => value.toLocaleString()} style={{fontSize: '12px', fill: 'white'}} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="table-section" style={{marginTop: '30px'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
          <h2 style={{margin: 0}}>Beneficiaries ({beneficiaries.length.toLocaleString()} records)</h2>
          <button 
            onClick={downloadCSV}
            style={{
              padding: '10px 20px',
              backgroundColor: '#4caf50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'background-color 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#45a049'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#4caf50'}
          >
            Download CSV
          </button>
        </div>
        <div style={{overflowX: 'auto', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
          <table style={{width: '100%', borderCollapse: 'collapse'}}>
            <thead>
              <tr style={{backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb'}}>
                {beneficiaries.length > 0 && Object.keys(beneficiaries[0]).map((column) => (
                  <th key={column} style={{padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap'}}>
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {beneficiaries.map((row, index) => (
                <tr key={`${row.BENE_MBI_ID}-${index}`} style={{borderBottom: '1px solid #e5e7eb', backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb'}}>
                  {Object.keys(row).map((column) => (
                    <td key={column} style={{padding: '12px', fontSize: '13px', color: '#1f2937', whiteSpace: 'nowrap'}}>
                      {row[column] !== null && row[column] !== undefined ? row[column] : '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </div>
      </div>
    </div>
  );
}

export default DemographicsDashboard;
